import { useEffect, useState } from 'react';
import { EXPECTED_ORG_ID, useAuthKit } from '../../contexts/AuthProvider';
import { loadedWithAuthCode, SIGN_IN_INCOMPLETE_MESSAGE } from '../../auth/authCallbackNotice';

/**
 * Marketing landing shell. Renders `public/landing.html` in a full-window
 * iframe so Preston's standalone site lives unchanged in our repo, and
 * bridges two paths to WorkOS sign-in:
 *
 *   1. `postMessage({ type: 'minetwin:login' })` from inside the iframe
 *      — the Login button in the landing's navbar posts this when
 *      clicked, so the user can sign in without leaving the page.
 *   2. `?login=1` query param on the parent URL — the same Login
 *      anchor's href falls back to `/?login=1` when the landing is
 *      opened standalone (no parent window). If that page reloads
 *      into our React shell, this effect picks up the param and
 *      kicks off signIn().
 *
 * No overlay button — the Login + Book a Pilot CTAs live inside the
 * iframe's navbar now so they appear side by side instead of one
 * floating outside the page chrome (Cloud Lead 2026-06-28).
 */
export function LandingPage() {
  const { signIn } = useAuthKit();
  const [showChooser, setShowChooser] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('login') === '1') {
      // Strip the param before opening the chooser so the back
      // button doesn't loop the user into sign-in again.
      url.searchParams.delete('login');
      window.history.replaceState({}, '', url.toString());
      setShowChooser(true);
      return;
    }
    function onMessage(ev: MessageEvent) {
      const data = ev.data as { type?: string } | null;
      if (data && data.type === 'minetwin:login') setShowChooser(true);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div className="landing-shell">
      <iframe src="/landing.html" title="MineTwin" className="landing-iframe" />
      {/* This load was a sign-in callback (`?code=`) yet there is no session
          (see auth/authCallbackNotice.ts). Bottom-centred so it never covers
          the iframe's navbar Login button; the chooser's backdrop replaces it. */}
      {loadedWithAuthCode() && !showChooser && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            zIndex: 40,
            maxWidth: 'calc(100vw - 32px)',
            padding: '10px 16px',
            borderRadius: 8,
            border: '1px solid #2A3B52',
            background: '#0B1622',
            color: '#E6EDF3',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 14,
          }}
        >
          {SIGN_IN_INCOMPLETE_MESSAGE}
        </div>
      )}
      {showChooser && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(4, 10, 18, 0.72)',
            zIndex: 50,
          }}
          onClick={() => setShowChooser(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0B1622',
              border: '1px solid #1E2A3A',
              borderRadius: 12,
              padding: '28px 32px',
              width: 340,
              display: 'grid',
              gap: 12,
              fontFamily: 'Inter, system-ui, sans-serif',
              color: '#E6EDF3',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Sign in to MineTwin</div>
            {/* Org-scoped sign-in (webclient pattern): scoping to the MineTwin
                customer org hard-routes to its Entra connection — the right
                path for @minetwin.ai staff, whose SSO-provisioned accounts
                have no WorkOS password. It MUST NOT be the only path: an
                org-scoped flow skips the default AuthKit page entirely, which
                locked out the password/Google accounts (Cloud Lead
                2026-07-29) — hence this two-button chooser. */}
            <button
              type="button"
              onClick={() => void signIn({ organizationId: EXPECTED_ORG_ID })}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#2F6FEB',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              MineTwin staff — sign in with Microsoft
            </button>
            <button
              type="button"
              onClick={() => void signIn()}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid #2A3B52',
                background: 'transparent',
                color: '#E6EDF3',
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Sign in with email / password
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
